import type { RevenueClosing, RevenueLedgerLatestRun } from '@cortex/domain-ac-mens-hub-api';
import { ORPCError } from '@orpc/client';
import { Button } from '@cortex/web-design-system/components/ui/Button';
import { Modal } from '@cortex/web-design-system/components/ui/Modal';
import { useTranslation } from 'react-i18next';
import { useCloseLedgerMonth } from '../../../hooks/useRevenueLedger.js';
import { formatSnapshotJST, formatYen } from '../../../lib/revenue-aggregation.js';

/**
 * 締め直しかどうか。見ている版で締めた印があるときだけ締め直しになる（版違いは新しく締める）。
 * @graph-connects none
 */
export function isReclose(closing: RevenueClosing | undefined): boolean {
  return closing?.state === 'closed' || closing?.state === 'closed_with_diff';
}

/**
 * API が理由を返す失敗（先に締められた・計算が更新された・月末前）は、その理由をそのまま出す。
 * それ以外（通信の失敗など）は一般の文言にする。
 * @graph-connects none
 */
function closeErrorMessage(error: Error, fallback: string): string {
  return error instanceof ORPCError && (error.code === 'CONFLICT' || error.code === 'BAD_REQUEST')
    ? error.message
    : fallback;
}

/**
 * @graph-node {Module}
 * @graph-stack ac-mens-hub
 * @graph-domain Finance
 * @graph-business 月を締める・締め直す前の確認モーダル。対象月・定義の版・確定にする朝の計算時刻・その計算の売上高の合計を見せ、他の人が先に締めた・計算が更新された・月末前のときは上書きせずに API の理由を知らせる。
 * @graph-connects useCloseLedgerMonth [calls] 月の締めの送信
 */
export function CloseMonthModal({
  month,
  closing,
  run,
  onClose,
}: {
  month: string;
  closing: RevenueClosing | undefined;
  run: RevenueLedgerLatestRun;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const mutation = useCloseLedgerMonth();
  const reclose = isReclose(closing);
  const previousRunId = reclose ? (closing?.closedRunId ?? null) : null;
  const errorMessage =
    mutation.error === null
      ? null
      : closeErrorMessage(mutation.error, t('revenue.ledger.closeFailed'));

  return (
    <Modal
      open
      onClose={onClose}
      title={t(reclose ? 'revenue.ledger.recloseModalTitle' : 'revenue.ledger.closeModalTitle', {
        month,
      })}
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            disabled={mutation.isPending}
            onClick={() => {
              mutation.mutate(
                { version: 'v0.1', month, runId: run.runId, previousRunId },
                { onSuccess: onClose },
              );
            }}
          >
            {t(reclose ? 'revenue.ledger.recloseConfirm' : 'revenue.ledger.closeConfirm')}
          </Button>
        </div>
      }
    >
      <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 text-sm">
        <dt className="text-[var(--color-text-secondary)]">
          {t('revenue.ledger.closeModalMonth')}
        </dt>
        <dd className="text-[var(--color-text-primary)]">{month}</dd>
        <dt className="text-[var(--color-text-secondary)]">
          {t('revenue.ledger.closeModalVersion')}
        </dt>
        <dd className="text-[var(--color-text-primary)]">v0.1</dd>
        <dt className="text-[var(--color-text-secondary)]">
          {t('revenue.ledger.closeModalComputedAt')}
        </dt>
        <dd className="text-[var(--color-text-primary)]">{formatSnapshotJST(run.computedAt)}</dd>
        <dt className="text-[var(--color-text-secondary)]">
          {t('revenue.ledger.closeModalSalesTotal')}
        </dt>
        <dd className="font-semibold tabular-nums text-[var(--color-text-primary)]">
          {formatYen(run.salesTotal)}
        </dd>
      </dl>
      {errorMessage !== null && (
        <p role="alert" className="mt-4 text-sm text-[var(--color-error)]">
          {errorMessage}
        </p>
      )}
    </Modal>
  );
}
